import { MELEE, PALETTE, VISUAL_ROLES } from "@eldrun/content";
import { QUALITY } from "../render/quality.ts";
import { AnimationMixer, Group, Quaternion, Vector3, type AnimationAction, type AnimationClip, type Mesh, type Object3D, type SkinnedMesh } from "three";
import type { AtmosphereUniforms } from "../render/atmosphere.ts";
import { AVATAR_CLIP_TABLE, type AvatarLibrary, type AvatarModelId } from "../render/avatar-library.ts";
import { applyCharacterSurface, type CharacterSurfaceHandle } from "../render/character-surface/apply.ts";
import { actorStyle, type CharacterSurfaceStyle } from "../render/character-surface/config.ts";
import { AVATAR_RIM } from "../render/shaders/avatar.ts";
import { AnimationLod, animationEvery } from "./avatar-lod.ts";
import { AvatarStateMachine, type AvatarClipName, type AvatarMix, type ClipSample } from "./avatar-state.ts";
import type { AvatarFrame, AvatarView, AvatarViewStats } from "./avatar-view.ts";
import { kaykitHand, poseHeld } from "./held-item.ts";

type Part = "lower" | "upper";

/** A ação de um clipe numa metade (a segunda dos gestos com `'`). */
const actionKey = (clip: string, part: Part, slot?: 1) => `${clip}:${part}${slot ? "'" : ""}`;

/** Nome do nó raiz dos jogadores na cena (a camada `avatar` do `drawBreakdown()`). */
export const AVATAR_LAYER = "avatar";

/** Quanto do olhar vai para o peito e para a cabeça, e o teto (rad): mirar o arco para o alto ou o chão. */
export const AIM = { chest: 0.35, head: 0.35, limit: 1.2 } as const;
const axis = new Vector3();
const parent = new Quaternion();
const turn = new Quaternion();

/**
 * Avatar animado (F49): um clone do personagem KayKit (uma `SkinnedMesh`, uma draw call) com a
 * superfície dos atores (F119, ADR 0025: o PBR com a cor do atlas, a máscara na cor da classe no shader,
 * linhas e nós; uniforms por ator), a variante de dissolve própria só enquanto some ou volta, e um `AnimationMixer` com as ações de cada metade de cada clipe já criadas (as ligações
 * nascem na pose de repouso), mais uma segunda ação da metade de cima de cada gesto (`ClipSample.slot`). A pose vem da máquina de estados (`AvatarStateMachine`): a cada
 * atualização, instante e peso de cada ação e um `mixer.update(0)`, sem relógio próprio do mixer. O LOD
 * (`AnimationLod`) decide em que quadros isso acontece; o tempo pulado entra no seguinte.
 */
export class SkinnedAvatar implements AvatarView {
  readonly root = new Group();
  readonly heldView = "kaykit" as const;
  private readonly model: AvatarModelId;
  private readonly mesh: SkinnedMesh;
  private readonly hands: Readonly<Record<"direita" | "esquerda", Object3D>>;
  private readonly surface: CharacterSurfaceHandle;
  private readonly mixer: AnimationMixer;
  private readonly actions = new Map<string, AnimationAction>();
  private readonly machine = new AvatarStateMachine(AVATAR_CLIP_TABLE);
  private readonly lod: AnimationLod;
  private held: Mesh | undefined;
  private mix: AvatarMix | undefined;
  private dissolveAmount = 0;
  /** Olhar para cima (+) ou para baixo (−), em rad, do último quadro animado. */
  private pitch = 0;
  /** Peito e cabeça, com a parte da mira de cada um e o giro do clipe sem ela. */
  private readonly aimed: { bone: Object3D; share: number; base: Quaternion }[];
  /** O nó do pacote (`avatar`) do clone: o espaço em que o boneco olha para −Z. */
  private readonly skeletonRoot: Object3D;

  /** `surface`: ajustes da superfície por cima dos do papel (o visor da F120: linhas, nós, semente). */
  constructor(library: AvatarLibrary, atmosphere: AtmosphereUniforms, model: AvatarModelId, hex: string | undefined, slot = 0, surface: Partial<CharacterSurfaceStyle> = {}) {
    // A cor do atlas inteira: a máscara toma a cor da classe no shader (segue a troca e o modo daltônico).
    const instance = library.instantiate(model);
    this.model = model;
    this.mesh = instance.mesh;
    // Os heróis projetam na sombra do sol (Fase 13; as criaturas animadas, não: `sun-shadow.ts`).
    this.mesh.userData["sunCaster"] = true;
    this.hands = { direita: instance.hand, esquerda: instance.leftHand };
    // O nome agrupa os avatares (malha e item na mão) no `drawBreakdown()`.
    this.root.name = AVATAR_LAYER;
    // Só o corpo: o item na mão entra depois, nos encaixes, e fica no material dele.
    this.surface = applyCharacterSurface(instance.root, { ...actorStyle("heroi", hex ?? PALETTE.neutro.hex), ...surface }, {
      atmosphere,
      look: { rim: AVATAR_RIM, nightRim: false, tintRef: hex === undefined ? 0 : library.tintRef(model) },
      include: (mesh) => mesh === instance.mesh,
    });
    this.root.add(instance.root);
    this.lod = new AnimationLod(slot);
    this.mixer = new AnimationMixer(instance.root);
    const add = (key: string, clip: AnimationClip) => {
      const action = this.mixer.clipAction(clip);
      action.weight = 0;
      action.play();
      this.actions.set(key, action);
    };
    for (const [name, parts] of library.clips) {
      for (const part of ["lower", "upper"] as const) add(actionKey(name, part), parts[part]);
      if (parts.echo) add(actionKey(name, "upper", 1), parts.echo);
    }
    this.skeletonRoot = instance.root;
    const shares: [string, number][] = [["chest", AIM.chest], ["head", AIM.head]];
    this.aimed = shares.flatMap(([name, share]) => {
      const bone = instance.root.getObjectByName(name);
      return bone ? [{ bone, share, base: bone.quaternion.clone() }] : [];
    });
    // As ligações nasceram no repouso (os braços abertos do KayKit); até a primeira atualização (longe
    // ou fora da tela, ela pode demorar), o avatar fica parado de pé, não em T.
    this.showClip("parado", 0);
  }

  update(f: AvatarFrame): void {
    this.root.rotation.set(0, f.yaw, 0);
    // Atualiza mesmo fora do LOD de animação: uma interrupção nunca deixa energia presa no corpo.
    this.setEnergy(f.signals.dead ? 0 : attackEnergy(f.signals.swing, f.signals.drawing));
    const dt = this.lod.step(f.dt, f.frame, animationEvery(f.distance, f.visible, f.range));
    if (dt === undefined) return;
    this.pitch = f.pitch;
    this.apply((this.mix = this.machine.step(f.signals, dt)));
  }

  /** Energia local (0..1), também usada pelas capturas determinísticas. */
  setEnergy(amount: number): void {
    const role = VISUAL_ROLES.heroi;
    const strength = Math.max(0, Math.min(1, amount)) * (QUALITY.post.reduceFlashes ? 0.25 : 1);
    this.surface.setSeam(role.accent + (role.accentPeak - role.accent) * strength);
  }

  /** A superfície do corpo (vitrines e testes). */
  get surfaceHandle(): CharacterSurfaceHandle {
    return this.surface;
  }

  /** A pose do último quadro animado (as amostras de cada metade), para as vitrines e os testes. */
  get pose(): AvatarMix | undefined {
    return this.mix;
  }

  /** Vitrines e testes: o clipe `clip` no corpo inteiro, parado no instante `time`, sem a máquina de estados (com a mira `pitch`). */
  showClip(clip: AvatarClipName, time: number, pitch = this.pitch): void {
    this.pitch = pitch;
    const sample = [{ clip, time, weight: 1 }];
    this.apply((this.mix = { lower: sample, upper: sample, state: "locomocao", gesture: undefined }));
  }

  /** Instante e peso de cada ação pela pose do quadro; o resto fica com peso 0. */
  private apply(mix: AvatarMix): void {
    for (const action of this.actions.values()) action.weight = 0;
    const set = (samples: readonly ClipSample[], part: Part) => {
      for (const s of samples) {
        const action = this.actions.get(actionKey(s.clip, part, s.slot));
        if (!action) continue;
        action.time = s.time;
        action.weight = s.weight;
      }
    };
    // O mixer só reescreve o osso cujo valor misturado mudou: a mira do quadro anterior sai antes.
    for (const a of this.aimed) a.bone.quaternion.copy(a.base);
    set(mix.lower, "lower");
    set(mix.upper, "upper");
    this.mixer.update(0);
    this.aim();
  }

  /**
   * A mira de cima a baixo no peito e na cabeça (`AIM`), por cima do clipe: cada osso gira em torno da
   * direita do boneco (+x da raiz dele, que olha para −Z) levada ao espaço do pai, então o olhar sobe e
   * desce mesmo com o tronco torcido (o arco puxado de lado). Guarda o giro do clipe para tirar a mira
   * na próxima atualização.
   */
  private aim(): void {
    const pitch = Math.max(-AIM.limit, Math.min(AIM.limit, this.pitch));
    for (const a of this.aimed) {
      a.base.copy(a.bone.quaternion);
      if (pitch === 0) continue;
      parent.identity();
      for (let o = a.bone.parent; o && o !== this.skeletonRoot; o = o.parent) parent.premultiply(o.quaternion);
      axis.set(1, 0, 0).applyQuaternion(parent.invert());
      a.bone.quaternion.premultiply(turn.setFromAxisAngle(axis, pitch * a.share));
    }
  }

  setDissolve(amount: number, edge = true): void {
    this.dissolveAmount = amount;
    // Uma variante por avatar (o limiar é dele), criada só quando ele some ou volta: o programa é um só.
    this.surface.setDissolve(amount, edge);
  }

  setHeld(mesh: Mesh | undefined, itemId = ""): void {
    this.held?.removeFromParent();
    this.held = mesh;
    if (mesh) {
      poseHeld(mesh);
      this.hands[kaykitHand(itemId)].add(mesh);
    }
  }

  stats(): AvatarViewStats {
    const top = (samples: readonly ClipSample[] | undefined): AvatarClipName | undefined => samples?.reduce<ClipSample | undefined>((best, s) => (best && best.weight >= s.weight ? best : s), undefined)?.clip;
    return {
      model: this.model,
      clip: top(this.mix?.lower) ?? "parado",
      ...(this.mix?.gesture && { gesture: this.mix.gesture }),
      lod: this.lod.every,
      dissolve: this.dissolveAmount,
      seen: { ...this.machine.entered },
    };
  }

  dispose(): void {
    this.mixer.stopAllAction();
    this.mixer.uncacheRoot(this.mixer.getRoot());
    this.mesh.skeleton.dispose();
    this.surface.dispose();
    this.root.removeFromParent();
  }
}

/** Smooth anticipation → release → recovery, aligned with the real melee impact fraction. */
export function attackEnergy(progress: number, drawing = false): number {
  if (progress <= 0 || progress >= 1) return drawing ? 0.3 : 0;
  const p = MELEE.impactFraction;
  const t = progress < p ? progress / p : (1 - progress) / (1 - p);
  return t * t * (3 - 2 * t);
}
